/**
 * Horographia — 시계 제작 공정 (교육용 요약).
 */

export type ProcessStep = {
  id: string;
  order: number;
  titleKo: string;
  titleEn: string;
  stage: 'design' | 'movement' | 'case' | 'dial' | 'assembly' | 'qc';
  summary: string;
  details: string[];
  historyNote: string;
};

export const PROCESS_STAGES: { id: ProcessStep['stage']; labelKo: string }[] = [
  { id: 'design', labelKo: '설계' },
  { id: 'movement', labelKo: '무브먼트' },
  { id: 'case', labelKo: '케이스·밴드' },
  { id: 'dial', labelKo: '다이얼·침' },
  { id: 'assembly', labelKo: '조립' },
  { id: 'qc', labelKo: '검사·출하' },
];

export const processSteps: ProcessStep[] = [
  {
    id: 'brief',
    order: 1,
    titleKo: '콘셉트·브리프',
    titleEn: 'Concept brief',
    stage: 'design',
    summary: '용도(다이빙·드레스·항공), 가격대, 사이즈, 컴플리케이션을 정한다.',
    details: [
      '타깃 사용자와 「도구 vs 장식」 축을 먼저 고른다.',
      '방수·두께·러그 폭 같은 제약이 미학보다 먼저 고정되기도 한다.',
      '레퍼런스 넘버·제품 패밀리 규칙을 브랜드 카탈로그에 맞춘다.',
    ],
    historyNote: '20세기 중반 이후 「라인 확장」이 일반화되면서 브리프가 문서화되는 경우가 늘었다.',
  },
  {
    id: 'cad',
    order: 2,
    titleKo: '도면·CAD·프로토타입',
    titleEn: 'CAD & prototype',
    stage: 'design',
    summary: '케이스·무브먼트 배치를 3D로 잡고, CNC·프린트로 시제품을 만든다.',
    details: [
      '크리스탈 곡률, 크라운 위치, 푸셔 간섭을 시뮬레이션한다.',
      '프로토타입으로 착용감·가독성을 확인한 뒤 양산 금형을 연다.',
    ],
    historyNote: '전통적으로는 수작업 도면과 황동 목업이 중심이었고, 1990년대 이후 CAD가 표준이 되었다.',
  },
  {
    id: 'blank',
    order: 3,
    titleKo: '무브먼트 블랭크·부품',
    titleEn: 'Movement blanks',
    stage: 'movement',
    summary: '플레이트·브리지·기어·스프링 등 부품을 절삭·스탬핑·전조로 만든다.',
    details: [
      '매뉴팩처는 핵심 부품을 자체 생산하고, 일부는 전문 공급사에서 받는다.',
      '피벗·홀은 미크론 단위 공차로 뚫는다.',
      '주얼(합성 루비)을 압입해 마찰을 줄인다.',
    ],
    historyNote: '19세기 스위스에서는 에바슈(반제품) 분업이 발달했고, 이후 수직 통합 매뉴팩처가 다시 늘었다.',
  },
  {
    id: 'finish-mv',
    order: 4,
    titleKo: '무브먼트 마감',
    titleEn: 'Movement finishing',
    stage: 'movement',
    summary: '페를라주·코트 드 제네브·앵글라주·블루 나사 등으로 표면을 다듬는다.',
    details: [
      '보이는 면과 안쪽 면의 마감 수준이 달라질 수 있다.',
      '하이엔드는 모서리를 손으로 깎아 빛 반사를 만든다.',
      '산업 라인은 기계 브러시·부분 폴리시로 효율을 낸다.',
    ],
    historyNote: '제네바 실(Poinçon de Genève) 등은 마감·조립 기준을 제도화한 사례다.',
  },
  {
    id: 'regulate',
    order: 5,
    titleKo: '조율·크로노미터 시험',
    titleEn: 'Regulation & timing',
    stage: 'movement',
    summary: '밸런스·헤어스프링을 조절해 일오차를 목표 범위에 넣는다.',
    details: [
      '자세(다이얼 위·아래·옆)별 오차를 본다.',
      '온도·이소크로니즘(진폭에 따른 주기 변화)을 점검한다.',
      'COSC 등 공인 시험은 별도 샘플·배치로 진행된다.',
    ],
    historyNote: '해상 크로노미터 전통에서 손목시계 크로노미터 인증으로 이어졌다.',
  },
  {
    id: 'case-machine',
    order: 6,
    titleKo: '케이스 가공',
    titleEn: 'Case machining',
    stage: 'case',
    summary: '강철·티타늄·골드 블록·단조재를 CNC·선반으로 깎아 미드케이스·러그를 만든다.',
    details: [
      '베젤·케이스백·튜브(크라운 구멍)를 별도 가공 후 맞춘다.',
      '세라믹 베젤은 소결·연마 후 눈금을 레이저·충전한다.',
      '방수 홈에 가스켓 자리를 정확히 판다.',
    ],
    historyNote: '롤렉스 오이스터(1926년경) 이후 스크류 구조 방수 케이스가 스포츠 워치의 표준 중 하나가 되었다.',
  },
  {
    id: 'case-finish',
    order: 7,
    titleKo: '케이스·브레이슬릿 마감',
    titleEn: 'Case & bracelet finishing',
    stage: 'case',
    summary: '브러시·폴리시·샌드블라스트·코팅으로 표면 질감을 만든다.',
    details: [
      '스포츠 럭셔리는 브러시와 폴리시를 교차해 엣지를 살린다.',
      '통합 브레이슬릿은 링크마다 각도를 맞추며 연마한다.',
      'PVD/DLC는 진공 챔버에서 증착한다.',
    ],
    historyNote: '1970년대 강철 스포츠 럭셔리(로열 오크 등)가 「도구 마감의 고급화」를 대중화했다.',
  },
  {
    id: 'dial-make',
    order: 8,
    titleKo: '다이얼 제작',
    titleEn: 'Dial making',
    stage: 'dial',
    summary: '황동·골드 판에 도금·래커·기로셰·인쇄·인덱스 부착을 한다.',
    details: [
      '날짜 창을 펀칭하고 가장자리를 다듬는다.',
      '아플리케 인덱스를 접착·나사 고정한다.',
      '루미(축광)를 침·인덱스에 올린다.',
    ],
    historyNote: '에나멜·엔진 터닝(기로셰)은 18~19세기 회중시계에서 손목시계로 이어진 장식 기술이다.',
  },
  {
    id: 'hands',
    order: 9,
    titleKo: '침 제작·부착',
    titleEn: 'Hands',
    stage: 'dial',
    summary: '스탬핑·절삭한 침에 도금·루미를 입히고, 캐논 피니언에 높이를 맞춰 끼운다.',
    details: [
      '시·분·초·크로노 침의 높이가 겹치지 않게 스택한다.',
      '마찰이 너무 세면 충격에 약하고, 너무 느슨하면 미끄러진다.',
    ],
    historyNote: '침 형태(메르세데스·스워드 등)는 브랜드·시대 가독성 철학을 드러낸다.',
  },
  {
    id: 'casing',
    order: 10,
    titleKo: '케이싱업',
    titleEn: 'Casing-up',
    stage: 'assembly',
    summary: '무브먼트를 케이스에 넣고 다이얼·침·크리스탈·크라운을 조립한다.',
    details: [
      '스템(용두 축) 길이를 케이스에 맞게 자른다.',
      '가스켓에 윤활을 바르고 스크류 백을 규정 토크로 조인다.',
      '크로노 푸셔·밸브 등 외부 부품을 순서대로 장착한다.',
    ],
    historyNote: '현대 매뉴팩처는 무균·정전기 관리된 조립실에서 라인·셀 방식으로 케이싱한다.',
  },
  {
    id: 'strap',
    order: 11,
    titleKo: '스트랩·브레이슬릿 장착',
    titleEn: 'Strap / bracelet fit',
    stage: 'assembly',
    summary: '스프링바·나사 링크로 밴드를 달고 클라스프를 맞춘다.',
    details: [
      '러그 폭과 끝 링크 곡률이 맞아야 유격·소음이 적다.',
      '가죽·고무는 수분·자외선에 약해 소모품으로 본다.',
    ],
    historyNote: '초기 손목시계는 가죽 끈이 일반적이었고, 금속 브레이슬릿은 20세기 중반 이후 스포츠와 함께 확산했다.',
  },
  {
    id: 'water-test',
    order: 12,
    titleKo: '방수·압력 시험',
    titleEn: 'Water resistance test',
    stage: 'qc',
    summary: '공기·수압으로 누설을 확인하고, 표시 방수 등급에 맞는 시험을 한다.',
    details: [
      '크라운을 잠근 상태에서만 의미가 있다.',
      '다이버 모델은 ISO 관련 절차를 따르는 경우가 많다.',
      '가스켓 노후 후 재시험이 필요하다.',
    ],
    historyNote: '군·산업 규격과 브랜드 자체 시험이 병행되며, 「m」 표시는 정적 압력 시험의 환산이다.',
  },
  {
    id: 'rate-qc',
    order: 13,
    titleKo: '성능·외관 QC',
    titleEn: 'Final QC',
    stage: 'qc',
    summary: '일오차, 파워 리저브, 기능 조작, 스크래치·정렬을 최종 확인한다.',
    details: [
      '크로노·GMT·캘린더 전환을 실제 조작으로 검증한다.',
      '다이얼 먼지·인덱스 기울기·베젤 클릭감을 본다.',
      '통과한 개체만 보증서·박스와 함께 출하된다.',
    ],
    historyNote: '대량 생산 시대에도 「개체별 카드」로 타이밍 결과를 남기는 브랜드가 있다.',
  },
];
